import {useEffect, useState} from 'react';
import {redeemOnboarding} from '../api/auth';

/**
 * Signs in a user who arrives from the onboarding site (igenfun.xyz) already
 * verified. Onboarding lands them on any path of ours with a one-time code in the
 * URL fragment - https://igenfun.com/pricing#t=CODE - which we exchange server-side
 * for a normal session.
 *
 * Mount this once, at the App level, above the router: the code lives in the
 * fragment, which react-router ignores, so a single App-level handler covers every
 * landing path and onboarding picks the destination just by choosing the URL.
 *
 * @param setTokenState  App's token setter (the localStorage write happens in redeemOnboarding)
 * @param setSignedIn    App's signed-in flag - flipping it is what re-runs fetchUser
 * @returns {{status: 'idle'|'redeeming'|'done'|'failed'}} gate the UI on 'redeeming'
 */

// The code is read - and stripped - at module load, before React renders anything:
//
//  - it is single-use, and StrictMode mounts effects twice in dev, so a second
//    redeem of the same code would get a 400 from the issuer;
//  - it is a credential for the 60s it lives, and must not survive into history,
//    a copied URL, a Referer, or an error report;
//  - redirectLink() in Pricing/Header parses window.location.href by splitting on
//    "/", so a fragment still hanging off the last path segment mangles the links
//    those pages build.
const HANDOFF = readHandoff();

function readHandoff() {
    if (typeof window === 'undefined') return null;
    let params;
    try {
        params = new URLSearchParams(window.location.hash.slice(1));
    } catch (e) {
        return null;
    }
    const code = params.get('t');
    if (!code) return null;

    // Keep the query string. The integration doc's snippet strips back to
    // pathname alone, which would drop ?ref= / utm_* - read in Landing, Header
    // and GenerationApp - and lose the attribution for the very users we are
    // trying to attribute.
    params.delete('t');
    const rest = params.toString();
    const clean = window.location.pathname + window.location.search + (rest ? `#${rest}` : '');
    try {
        window.history.replaceState(null, '', clean);
    } catch (e) {
        /* non-fatal: the redeem still works, the URL just stays dirty */
    }
    return {code, promise: null};
}

// Attribution the onboarding site could not pass us any other way: prefer a ref on
// the landing URL (onboarding can forward its own), fall back to whatever this
// browser already recorded.
function resolveRef() {
    try {
        return new URLSearchParams(window.location.search).get('ref')
            || localStorage.getItem('referral_code')
            || null;
    } catch (e) {
        return null;
    }
}

async function redeem() {
    const code = HANDOFF.code;
    HANDOFF.code = null;  // never reachable again, even from a debugger
    try {
        const [data, status] = await redeemOnboarding(code, resolveRef());
        if (status === 200 && data && data.access_token) {
            // The account may have been created on the onboarding side with a ref we
            // never saw; adopt it so payments and funnels attribute the same way.
            if (data.ref) {
                try {
                    if (!localStorage.getItem('referral_code')) {
                        localStorage.setItem('referral_code', data.ref);
                        // capture time: without it the site treats the code as stale
                        localStorage.setItem('referral_code_ts', String(Date.now()));
                    }
                } catch (e) { /* private mode */ }
            }
            return {ok: true, token: data.access_token};
        }
        // 400 (expired / already spent / wrong audience) and 503 alike: there is
        // nothing to retry into. Fall through to the normal logged-out page.
        console.warn('[Handoff] sign-in link not accepted', status);
        return {ok: false};
    } catch (e) {
        console.warn('[Handoff] redeem failed', e);
        return {ok: false};
    }
}

export function useOnboardingHandoff({setTokenState, setSignedIn} = {}) {
    const [status, setStatus] = useState(HANDOFF ? 'redeeming' : 'idle');

    useEffect(() => {
        if (!HANDOFF) return;
        let cancelled = false;
        // One redeem per page load, shared by every mount: under StrictMode the
        // second mount awaits the first call's promise instead of spending a code
        // that no longer exists (and instead of hanging on a spinner forever).
        if (!HANDOFF.promise) HANDOFF.promise = redeem();
        HANDOFF.promise.then((result) => {
            if (cancelled) return;
            if (result.ok) {
                // Same promotion the in-app onboarding does: token into React state,
                // then flip signedIn - App's bootstrap effect is keyed on it, and
                // without the flip `user` stays null and pages that check
                // `token && user` still treat this session as logged out.
                if (setTokenState) setTokenState(result.token);
                if (setSignedIn) setSignedIn(true);
                setStatus('done');
            } else {
                setStatus('failed');
            }
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {status};
}
